<template>
	<div
		class="timeline__end-of-timeline"
		v-observe-visibility="{
			callback: visibilityChanged,
			once: true,
		}"
		:style="reelBackground"
	>
		<h3>{{ emptyStateContent.headline }}</h3>
		<p v-if="hasFiltersActive" class="text-center">{{ emptyStateContent.text }}</p>
		<BasicButton color="primary" :rounded="true" :filled="false" :bordered="true" @click="clicked">
			<span>{{ btnText }}</span>
		</BasicButton>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { FilterHintEvents } from '@/enums/events';
import { isAnyFilterActive } from '@/helpers/filter-helper';
import BasicButton from '@/ui-components/BasicButton.vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { getDefaultFilterCollection } from '@/stores/useFilterStoreTypes';
import type { FilterName } from '@/stores/useFilterStoreTypes';

const props = withDefaults(
	defineProps<{
		hasNoTitles: boolean;
		isAtEndOfList: boolean;
	}>(),
	{
		hasNoTitles: false,
		isAtEndOfList: false,
	}
);

const emits = defineEmits(['onScrollToTop']);

const { t } = useI18n();
const router = useRouter();

const filterStore = useFilterStore();

const filterNames = Object.keys(getDefaultFilterCollection()).filter(
	filterName => !['content_types', 'inner_tab'].includes(filterName)
) as FilterName[];

const hasFiltersActive = computed(() => {
	const filters: FilterName[] = [...Object.keys(filterStore.currentFilters)] as FilterName[];
	return isAnyFilterActive(filters, filterStore.currentFilters);
});

const btnText = computed(() => {
	return hasFiltersActive.value ? t('WEBAPP_CLEAR_FILTERS') : t('WEBAPP_GO_TO_TOP');
});

const reelBackground = computed(() => {
	return {
		'background-image': `url('/${ASSETS_DIR}/img/film-reel.png')`,
		'background-repeat': 'no-repeat',
		'background-position-x': 'center',
	};
});

function visibilityChanged(isVisible: boolean) {
	if (isVisible) {
		trackEvent(FilterHintEvents.SHOWN);
	}
}

const emptyStateContent = computed(() => {
	if (props.hasNoTitles) {
		return {
			headline: t('WEBAPP_TIMELINE_NO_RESULTS_HEADLINE'),
			text: t('WEBAPP_TIMELINE_NO_RESULTS_TEXT'),
		};
	} else if (props.isAtEndOfList) {
		return {
			headline: t('WEBAPP_TIMELINE_END_OF_TIMELINE_HEADLINE'),
			text: t('WEBAPP_TIMELINE_END_OF_TIMELINE_TEXT'),
		};
	} else {
		return { headline: '', text: '' };
	}
});

onMounted(() => {
	trackEvent(FilterHintEvents.LOADED);
});

function clicked() {
	if (hasFiltersActive.value) {
		let newRoute;

		if (filterNames.length) {
			newRoute = filterStore.resetFilters({ filterNames });
		} else {
			filterStore.reset();
		}

		if (newRoute) router.push(newRoute);
	} else {
		emits('onScrollToTop');
	}
}

function trackEvent(action: FilterHintEvents): void {
	const label =
		(props.isAtEndOfList && FilterHintEvents.NO_MORE_TITLES) ||
		(props.hasNoTitles && FilterHintEvents.NO_TITLES) ||
		'';

	const properties: TrackingProviderPropertiesInterface = { action, label };
	if ([FilterHintEvents.SHOWN, FilterHintEvents.LOADED].includes(action)) properties.nonInteraction = true;

	TrackingHelper.trackEvent('filter_hint', properties);
}
</script>

<style lang="scss">
.timeline__end-of-timeline {
	text-align: center;
	margin: 10% 0 0;
	padding: 50px 4% 4%;

	h3 {
		margin-bottom: 8px;
	}

	h3 {
		margin-bottom: 8px;
	}

	& p {
		margin: 0 0 14px;
		padding: 0 10px 10px;
	}

	.text-center {
		text-align: center !important;
	}
}
</style>
